import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Text,
    Toolbar,
    ToolbarButton,
    Button,
    Input
} from '@fluentui/react-components';
import { DocumentData20Regular, ArrowLeft20Regular, ArrowDownload20Regular, DocumentPdf20Regular } from '@fluentui/react-icons';
import { useAppStyles } from '../../styles/appStyles';
import { useReportsPageStyles } from '../../styles/reportsPageStyles';
import EupsSelector from '../EupsSelector';
import ProgressPage from "../ProgressPage";
import { useKeoPrint } from "../../hooks/useKeoPrint";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { config } from "../../../config/environment";
import { EupType } from "../../../model";
import { useDevice } from '../../../model/DeviceContext';
import { bdoClient } from '../../../services/ApiClients';

import { isValidKeoCardNumber, extractYearFromKeoCardNumber, extractPointIdFromKeoCardNumber } from '@carbowaste/excel-sheets-generator/dist/sheets/common';

const appInsights = new ApplicationInsights({
    config: { instrumentationKey: config.appInsights.instrumentationKey }
});
appInsights.loadAppInsights();

interface CardKEOProps {
    onBack: () => void;
    companyId: string | null;
}

const CardKEO: React.FC<CardKEOProps> = ({ onBack, companyId }) => {
    const { t } = useTranslation();
    const styles = useAppStyles();
    const reportStyles = useReportsPageStyles();
    const { subscription, isTrial } = useDevice();
    const ReportId = "CW05";

    // Stany formularza
    const [cardNumber, setCardNumber] = useState("");
    const [selectedEupId, setSelectedEupId] = useState<string | null>(null);
    const [dateError, setDateError] = useState<string | null>(null);
    const [identificationNumberError, setIdentificationNumberError] = useState<string | null>(null);

    // Stany dla pobierania PDF
    const [generatedSheetName, setGeneratedSheetName] = useState<string | null>(null);
    const [generatedKeoId, setGeneratedKeoId] = useState<string | null>(null);
    const [generatedEupType, setGeneratedEupType] = useState<EupType | null>(null);
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
    const [pdfDownloadError, setPdfDownloadError] = useState<string | null>(null);
    // Ref do przechowania danych podczas generowania (ustawione dopiero po sukcesie)
    const pendingGenerationData = useRef<{ sheetName: string; eupType: EupType } | null>(null);
    // Sprawdź zaznaczoną komórkę po załadowaniu komponentu
    useEffect(() => {
        console.log("🔍 PrintCardKEO: useEffect checking for KEO number from active cell");

        const checkActiveCell = async () => {
            try {
                // Bezpośrednio sprawdź zaznaczoną komórkę w Excel
                if (typeof Office !== "undefined" && Office.context) {
                    await Excel.run(async (context) => {
                        console.log("📊 Excel.run started - checking selected range");
                        
                        // Odczytaj zaznaczoną komórkę
                        const range = context.workbook.getSelectedRange();
                        range.load("values, address");
                        await context.sync();
                        
                        const cellValue = range.values[0][0]?.toString().trim() || "";
                        console.log("📝 Active cell value:", cellValue, "from range:", range.address);
                        
                        // Sprawdź czy wartość to poprawny numer KEO
                        if (cellValue && isValidKeoCardNumber(cellValue)) {
                            console.log("✅ Auto-filling cardNumber from active cell:", cellValue);
                            setCardNumber(cellValue);

                            // Wyczyść wszystkie poprzednie błędy
                            setDateError(null);
                            setIdentificationNumberError(null);
                            setPdfDownloadError(null);

                            // Wyszukaj company i EUP na podstawie identificationNumber
                            findAndSetCompanyAndEupFromKeoNumber(cellValue);
                        } else if (cellValue) {
                            console.log("⚠️ Active cell contains value but it's not a valid KEO number:", cellValue);
                        } else {
                            console.log("� Active cell is empty");
                        }
                    });
                }
            } catch (error) {
                console.error("❌ Error reading active cell:", error);
            }
        };

        // Sprawdź aktywną komórkę po załadowaniu komponentu
        checkActiveCell();
    }, [subscription]);

    // Funkcja walidacji numeru KEO (przeniesiona z commands.ts)
    const isValidKeoCardNumber = (keoCardNumber: string): boolean => {
        const regex = /^\d{5}\/\d{4}\/KEO\/\d{4}\/\d{9}$/;
        return regex.test(keoCardNumber);
    };
    // Helper function: Wyszukaj Company i EUP po identificationNumber z numeru KEO
    const findAndSetCompanyAndEupFromKeoNumber = (keoNumber: string) => {
        if (!subscription || !subscription.companies) {
            console.log("⚠️ No subscription or companies available for lookup");
            return;
        }

        try {
            // Extract identificationNumber from KEO: split("/")[3] + "/" + split("/")[4]
            const parts = keoNumber.split("/");
            if (parts.length < 5) {
                console.log("❌ Invalid KEO format for identificationNumber extraction:", keoNumber);
                return;
            }

            const identificationNumber = parts[3] + "/" + parts[4];
            console.log("🔍 Looking for EUP with identificationNumber:", identificationNumber);

            // Search through all companies and their EUPs
            for (const company of subscription.companies) {
                if (!company.eups || company.eups.length === 0) continue;

                for (const eup of company.eups) {
                    if (eup.identificationNumber === identificationNumber) {
                        console.log("✅ Found matching EUP:", {
                            companyId: company.companyId,
                            companyName: company.name,
                            eupId: eup.eupId,
                            eupName: eup.name,
                            identificationNumber: eup.identificationNumber
                        });

                        // Clear any previous error
                        setIdentificationNumberError(null);

                        // Update localStorage with found company and EUP
                        try {
                            localStorage.setItem('carbowaste_selected_company_id', company.companyId);
                            console.log("✅ Updated localStorage: carbowaste_selected_company_id =", company.companyId);

                            // Also set selected EUP (może być potrzebne dla EupsSelector)
                            setSelectedEupId(eup.eupId);
                            console.log("✅ Set selectedEupId:", eup.eupId);
                        } catch (storageError) {
                            console.error("❌ Failed to update localStorage:", storageError);
                        }

                        return; // Found, stop searching
                    }
                }
            }

            // No matching EUP found - set error and do NOT update localStorage
            console.log("⚠️ No EUP found with identificationNumber:", identificationNumber);
            setIdentificationNumberError(
                t(
                    "keoCard_report.card_no_mpd_match",
                    "Karta KEO nie jest powiązana z zarejestrowanymi w aplikacji MPD"
                ) + `<br><small>Szukany numer MPD: ${identificationNumber}</small>`
            );
        } catch (error) {
            console.error("❌ Error in findAndSetCompanyAndEupFromKeoNumber:", error);
        }
    };

    // Handler dla zmiany EUP - automatycznie ustawia selected
    const handleEupChange = (eupId: string | null) => {
        setSelectedEupId(eupId);
    };

    // Hook dla KEO data print
    const {
        showProgress,
        fetchDataFunction,
        fetchKeoPrintData,
        handleProgressComplete,
        lastGenerationSuccess,
        lastGeneratedCardNumber,
        lastCardMetadata,
        resetProgress,
    } = useKeoPrint(() => {
        // Callback wywoływany po zakończeniu (tylko przy sukcesie)
        console.log('KEO report generation completed successfully');

        // Ustaw dane PDF dopiero teraz (po sukcesie)
        if (pendingGenerationData.current) {
            setGeneratedSheetName(pendingGenerationData.current.sheetName);
            setGeneratedEupType(pendingGenerationData.current.eupType);
            pendingGenerationData.current = null; // Wyczyść po użyciu
        }
    });

    // Funkcja obsługująca generowanie raportu
    const handleGenerateKeoReport = () => {
        appInsights.trackEvent({ name: 'CardKEO_GenerateReport_Clicked', properties: { cardNumber: cardNumber, eupId: selectedEupId } });

        // Wyczyść stare dane PDF przed rozpoczęciem nowego generowania
        setGeneratedSheetName(null);
        setGeneratedEupType(null);
        setPdfDownloadError(null);

        if (!isValidKeoCardNumber(cardNumber)) {
            setDateError(t("keoCard_report.card_invalid", "Niepoprawny numer karty KEO"));
            return;
        } else {
            setDateError(null);
        }


        // Pobierz dane uwierzytelniające dla wybranego EupId
        // Znajdź dane firmy i EUP
        const company = subscription?.companies?.find(c => c.companyId === companyId);
        const eup = company?.eups?.find(e => e.eupId === selectedEupId);

        if (!company || !eup) {
            console.error("Nie znaleziono danych firmy lub EUP");
            return;
        }

        // Sprawdzenie metodą extractPointIdFromKeoCardNumber czy pointId z numeru karty KEO zgadza się z identificationNumber EUP
        const pointIdFromCard = cardNumber.split("/")[3] + "/" + cardNumber.split("/")[4]; // Wyciągamy numer punktu z numeru karty KEO
        if (pointIdFromCard !== eup.identificationNumber) {
            setDateError(t("keoCard_report.card_pointid_mismatch") + `<br>Numer MPD z karty KEO: ${pointIdFromCard} <br>Numer wybranego MPD: ${eup.identificationNumber}`);
            return;
        } else {
            setDateError(null);
        }

        // Utworzenie eupType na podstawie danych z subscription
        const eupType: EupType = {
            bdoentityname: company.name, // Dodane pole
            eupId: eup.eupId,
            identificationNumber: eup.identificationNumber,
            name: eup.name,
            clientId: company.clientId,
            clientSecret: company.clientSecret,
            isTrial: isTrial
        };
        let year: number = extractYearFromKeoCardNumber(cardNumber);
        let sheetName: string = cardNumber.replace(/\//g, "_"); // Domyślna nazwa arkusza to numer karty KEO z zamienionymi ukośnikami

        // Zapisz dane tymczasowo - zostaną ustawione dopiero po sukcesie
        pendingGenerationData.current = { sheetName, eupType };

        // Wywołaj funkcję pobierania danych
        fetchKeoPrintData(
            eupType,
            cardNumber,
            sheetName,
        );

    };

    // Funkcja pobierająca PDF z BDO
    const handleDownloadPdf = async () => {
        if (!generatedEupType || !generatedSheetName || !lastGeneratedCardNumber) {
            console.error("Brak wymaganych danych do pobrania PDF");
            setPdfDownloadError(t("keoCard_report.pdf_download_error", "Brak wymaganych danych do pobrania PDF"));
            return;
        }

        setIsDownloadingPdf(true);
        setPdfDownloadError(null);
        appInsights.trackEvent({ name: 'CardKEO_DownloadPdf_Clicked', properties: { cardNumber: lastGeneratedCardNumber } });

        try {
            // Użyj keoId z metadata zamiast ponownie pobierać wszystkie karty
            if (!lastCardMetadata || !lastCardMetadata.keoId) {
                console.error("Brak metadanych karty KEO");
                setPdfDownloadError(t("keoCard_report.pdf_download_error", "Nie udało się znaleźć karty KEO w systemie BDO"));
                return;
            }

            // Pobierz PDF używając keoId z metadata
            const result = await bdoClient.downloadKeoPdf(
                generatedEupType.clientId,
                generatedEupType.clientSecret,
                generatedEupType.eupId,
                lastCardMetadata.keoId,
                `${generatedSheetName}.pdf`
            );

            if (result.error || !result.data) {
                console.error("Błąd pobierania PDF:", result.error);
                setPdfDownloadError(result.error || t("kpoCard_report.pdf_download_error", "Nie udało się pobrać pliku PDF z systemu BDO"));
                appInsights.trackEvent({
                    name: 'CardKPO_DownloadPdf_Error',
                    properties: { cardNumber: lastGeneratedCardNumber, error: result.error || 'unknown' }
                });
                return;
            }

            // Dekoduj Base64 do PDF i zapisz
            const base64Data = result.data;
            const binaryString = atob(base64Data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: 'application/pdf' });

            // Utwórz link do pobrania - użyj filename z wyniku
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = result.filename; // Użyj filename z result
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            appInsights.trackEvent({
                name: 'CardKPO_DownloadPdf_Success',
                properties: { cardNumber: lastGeneratedCardNumber, filename: result.filename }
            });

            console.log("PDF został pobrany:", result.filename);
        } catch (error) {
            console.error("Błąd podczas pobierania PDF:", error);
            setPdfDownloadError((error as Error).message || t("kpoCard_report.pdf_download_error", "Wystąpił błąd podczas pobierania pliku PDF"));
            appInsights.trackException({
                exception: error as Error,
                properties: { method: 'handleDownloadPdf', cardNumber: lastGeneratedCardNumber }
            });
        } finally {
            setIsDownloadingPdf(false);
        }
    };

    // Sprawdź czy można włączyć przycisk generowania
    const canGenerate = isValidKeoCardNumber(cardNumber) && selectedEupId !== null && !identificationNumberError;
    // Jeśli pokazujemy progress, renderuj ProgressPage
    if (showProgress && fetchDataFunction) {
        // Pobierz nazwę EUP dla wyświetlenia
        const company = subscription?.companies?.find(c => c.companyId === companyId);
        const eup = company?.eups?.find(e => e.eupId === selectedEupId);
        const eupDisplayName = eup ? `${eup.identificationNumber} - ${eup.name}` : undefined;

        return (
            <ProgressPage
                fetchData={fetchDataFunction}
                loadingMessage="Generowanie raportu KEO..."
                successMessage={t("report_success_keo_generated")}
                backTo="/"
                autoReturnDelay={3000}
                onComplete={handleProgressComplete}
                reportCode={ReportId}
                reportName={t('AccordionHeader_control_reports_option5', 'Podgląd karty KEO')}
                eupName={eupDisplayName}
                additionalInfo="Generowanie raportu może potrwać dłuższą chwilę..."
            />
        );
    }

    return (
        <div className={styles.appPageContainer}>
            <div className={styles.appPageHeader}>
                <DocumentData20Regular className={styles.appPageHeaderIcon} />
                <Text className={styles.appPageTitle}>
                    {ReportId} - {t('AccordionHeader_control_reports_option5', 'Podgląd karty KEO')}
                </Text>
            </div>

            <Toolbar className={styles.toolbar}>
                <ToolbarButton
                    className={styles.toolbarButton}
                    aria-label={t('common.back', 'Powrót')}
                    icon={<ArrowLeft20Regular className={styles.backIcon} />}
                    onClick={onBack}
                >
                    {t('common.back', 'Powrót')}
                </ToolbarButton>
            </Toolbar>
            {companyId && (
                <EupsSelector
                    companyId={companyId}
                    onEupChange={handleEupChange}
                    initialSelectedEupId={selectedEupId}
                />
            )}
            <div className={reportStyles.content}>
                {/* Wprowadzanie numeru karty KEO - cardNumber ręcznie do pola tekstowego wraz z informacją w trakcie wprowadzania czy numer jest poprawny, walidacja z urzyciem metody: isValidKeoCardNumber */}

                {/* Imput numeru karty KEO - cardNumber */}
                <div style={{ marginBottom: "16px" }}>
                    <Text weight="semibold" style={{ display: "block", marginBottom: "8px" }}>
                        {t("keoCard_report.card_label", "Numer karty KEO")}
                        <span style={{ color: "#a80000" }}> *</span>
                    </Text>
                    <Input
                        type="text"
                        value={cardNumber}
                        onChange={(e, data) => {
                            setCardNumber(data.value);
                            // Clear errors when user types
                            setIdentificationNumberError(null);
                            setDateError(null);
                            // Clear PDF section and success state when card number changes
                            if (data.value !== lastGeneratedCardNumber) {
                                setGeneratedSheetName(null);
                                setGeneratedEupType(null);
                                setPdfDownloadError(null);
                                resetProgress(); // Clear lastGenerationSuccess
                            }
                        }}
                        placeholder={t("keoCard_report.card_placeholder", "Wpisz numer karty KEO")}
                        pattern="[0-9]{5}/[0-9]{4}/KEO/[0-9]{4}/[0-9]{9}"
                        maxLength={29}

                        style={{ width: "100%" }}
                    />
                    {!isValidKeoCardNumber(cardNumber) && cardNumber.length > 0 && (
                        <Text size={200} style={{ color: "#a80000", marginTop: "4px", display: "block" }}>
                            {t("keoCard_report.card_invalid", "Niepoprawny numer karty KEO")}
                        </Text>
                    )}
                    <Text size={200} style={{ color: "#666", marginTop: "4px", display: "block" }}>
                        {t("keoCard_report.card_format_info", "Poprawny format: XXXXX/YYYY/KEO/XXXX/XXXXXXXXX. Gdzie X to cyfry, a Y to rok.")}
                    </Text>
                    {dateError && (
                        <div
                            style={{ color: "#d32f2f", marginTop: "4px", fontSize: "12px" }}
                            dangerouslySetInnerHTML={{ __html: dateError }}
                        />
                    )}
                    {identificationNumberError && (
                        <div
                            style={{ color: "#d32f2f", marginTop: "4px", fontSize: "12px", fontWeight: 600 }}
                            dangerouslySetInnerHTML={{ __html: identificationNumberError }}
                        />
                    )}

                </div>



                <div className={reportStyles.buttonWrapper} style={{ marginTop: "32px" }}>
                    <Button
                        appearance="primary"
                        className={styles.buttonBottom}
                        disabled={!canGenerate}
                        onClick={() => handleGenerateKeoReport()}
                        icon={<ArrowDownload20Regular />}
                    >
                        {t("keoCard_report.generate_button", "Generuj podgląd karty KEO")}
                    </Button>
                </div>

                {lastGenerationSuccess && lastGeneratedCardNumber === cardNumber && (
                    <div style={{ marginTop: "32px", padding: "16px", backgroundColor: "#f5f5f5", borderRadius: "8px" }}>
                        <Text style={{ display: "block", marginBottom: "16px", fontSize: "14px" }}>
                            {t("keoCard_report.bdo_documents_header", "Dokumenty z systemu BDO dostępne dla karty:")} <strong>{lastGeneratedCardNumber}</strong>
                        </Text>
                        {pdfDownloadError && (
                            <div style={{
                                marginBottom: "12px",
                                padding: "8px 12px",
                                backgroundColor: "#fff3cd",
                                borderLeft: "4px solid #ffc107",
                                borderRadius: "4px"
                            }}>
                                <Text style={{ color: "#856404", fontSize: "12px" }}>
                                    {pdfDownloadError}
                                </Text>
                            </div>
                        )}

                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "12px 16px",
                            backgroundColor: "#ffffff",
                            borderRadius: "6px",
                            border: "1px solid #e0e0e0",
                            cursor: isDownloadingPdf ? "wait" : "pointer",
                            transition: "all 0.2s ease",
                        }}
                            onClick={!isDownloadingPdf ? handleDownloadPdf : undefined}
                            onMouseEnter={(e) => {
                                if (!isDownloadingPdf) {
                                    e.currentTarget.style.backgroundColor = "#f0f0f0";
                                    e.currentTarget.style.borderColor = "#0078d4";
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "#ffffff";
                                e.currentTarget.style.borderColor = "#e0e0e0";
                            }}
                        >
                            <DocumentPdf20Regular style={{
                                marginRight: "12px",
                                color: "#d32f2f",
                                fontSize: "24px"
                            }} />
                            <Text style={{
                                flex: 1,
                                color: isDownloadingPdf ? "#999" : "#0078d4",
                                textDecoration: isDownloadingPdf ? "none" : "underline"
                            }}>
                                {isDownloadingPdf
                                    ? t("keoCard_report.downloading_pdf", "Pobieranie...")
                                    : t("keoCard_report.download_pdf_link", "Wydruk karty z systemu BDO")
                                }
                            </Text>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );


}
export default CardKEO;         